import { useEffect, useRef } from "react"

import { Box, CircularProgress, Stack, Typography } from "@mui/material"
import { STATUS } from "api/reactQuery/queries/tasks.types"
import { useTranslation } from "react-i18next"

interface IProps {
  status?: STATUS
  onComplete?: () => void
  onError?: () => void
  message?: string
  // For overlays taller than the screen: keep the message in view while
  // scrolling instead of centring it on the whole overlay.
  isMessageSticky?: boolean
}

const ProcessingOverlay: React.FC<IProps> = ({
  status,
  onComplete,
  onError,
  message,
  isMessageSticky = false,
}) => {
  const { t } = useTranslation()

  // Keep the latest callbacks in refs so the effect below only re-runs when
  // `status` itself changes, never when the parent re-renders with a fresh
  // (non-memoized) onComplete/onError reference.
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocusedElement =
      document.activeElement as HTMLElement | null
    containerRef.current?.focus()

    return () => {
      if (
        previouslyFocusedElement &&
        document.body.contains(previouslyFocusedElement)
      ) {
        previouslyFocusedElement.focus()
      }
    }
  }, [])

  useEffect(() => {
    if (status === "SUCCESS") {
      onCompleteRef.current?.()
    } else if (status === "FAILURE") {
      onErrorRef.current?.()
    }
  }, [status])

  return (
    <Box
      ref={containerRef}
      tabIndex={-1}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isMessageSticky ? "flex-start" : "center",
        gap: 2,
        backgroundColor: "white",
        zIndex: 1200,
        pointerEvents: "auto",
        overflow: isMessageSticky ? "visible" : "hidden",
        "&:focus": {
          outline: "none",
        },
      }}
    >
      <Stack
        role="status"
        aria-live="polite"
        spacing={2}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "400px",
          px: 2,
          ...(isMessageSticky && { position: "sticky", top: "40vh", py: 4 }),
        }}
      >
        <Typography
          variant="subtitle1"
          color="mockup.neutral10"
          fontWeight={500}
          sx={{ fontSize: "16px" }}
        >
          {message ?? t("students.processing")}
        </Typography>
        <CircularProgress aria-label={t("students.processing")} />
      </Stack>
    </Box>
  )
}

export default ProcessingOverlay
